// useFbPixel.js - Vue3 Meta Pixel 集成封装

/**
 * Meta Pixel Vue3 集成工具
 * 提供标准事件跟踪、自定义事件和页面浏览跟踪功能
 */

// 默认配置
const defaultConfig = {
    pixelId: null,
    debug: false,
    autoPageView: true,
    autoInit: true
};

// 全局状态
let isInitialized = false;
let isEnabled = true;
let config = { ...defaultConfig };

// 2026-10-26 广告同意：没有 ads 同意时不得初始化/发送 Pixel
function hasAdConsent() {
    if (typeof window === 'undefined') return false;
    if (window.__srAdsGranted === true) return true;
    try {
        const m = document.cookie.match(/(?:^|; )sr_consent=([^;]*)/);
        if (!m) return false;
        const data = JSON.parse(decodeURIComponent(m[1]));
        return Number(data.ads) === 1;
    } catch (e) {
        return false;
    }
}

/**
 * 初始化 Meta Pixel
 * @param {Object} options 配置选项
 */
export const initFbPixel = (options = {}) => {
    // 合并配置
    config = { ...defaultConfig, ...options };

    // 检查 Pixel ID
    if (!config.pixelId) {
        console.error('Meta Pixel: Pixel ID 未提供');
        return false;
    }

    if (!hasAdConsent()) {
        return false;
    }

    // 如果已经初始化，直接返回
    if (isInitialized) {
        if (config.debug) console.log('Meta Pixel: 已经初始化');
        return true;
    }

    // 添加 Meta Pixel 基础代码
    if (!window.fbq) {
        (function(f, b, e, v, n, t, s) {
            if (f.fbq) return;
            n = f.fbq = function() {
                n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
            };
            if (!f._fbq) f._fbq = n;
            n.push = n;
            n.loaded = !0;
            n.version = '2.0';
            n.queue = [];
            t = b.createElement(e);
            t.async = !0;
            t.src = v;
            s = b.getElementsByTagName(e)[0];
            s.parentNode.insertBefore(t, s);
        })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');
    }

    // 初始化 Pixel
    window.fbq('init', config.pixelId);

    // 自动发送页面浏览事件
    if (config.autoPageView) {
        window.fbq('track', 'PageView');
    }

    isInitialized = true;

    if (config.debug) {
        console.log('Meta Pixel: 初始化成功', config.pixelId);
    }

    return true;
};

/**
 * 跟踪标准事件
 * @param {string} eventName 事件名称
 * @param {Object} data 事件数据
 */
export const trackEvent = (eventName, data = {}) => {
    if (!isEnabled || !hasAdConsent()) return false;

    if (!isInitialized) {
        if (config.autoInit) {
            initFbPixel(config);
        } else {
            console.error('Meta Pixel: 未初始化');
            return false;
        }
    }

    if (typeof window.fbq === 'undefined') {
        console.error('Meta Pixel: fbq 未定义');
        return false;
    }

    try {
        window.fbq('track', eventName, data);

        if (config.debug) {
            console.log('Meta Pixel: 事件已发送', eventName, data);
        }

        return true;
    } catch (error) {
        console.error('Meta Pixel: 事件发送失败', error);
        return false;
    }
};

/**
 * 跟踪自定义事件
 * @param {string} eventName 自定义事件名称
 * @param {Object} data 事件数据
 */
export const trackCustomEvent = (eventName, data = {}) => {
    if (!isEnabled || !hasAdConsent()) return false;

    if (!isInitialized) {
        if (config.autoInit) {
            initFbPixel(config);
        } else {
            console.error('Meta Pixel: 未初始化');
            return false;
        }
    }

    if (typeof window.fbq === 'undefined') {
        console.error('Meta Pixel: fbq 未定义');
        return false;
    }

    try {
        window.fbq('trackCustom', eventName, data);

        if (config.debug) {
            console.log('Meta Pixel: 自定义事件已发送', eventName, data);
        }

        return true;
    } catch (error) {
        console.error('Meta Pixel: 自定义事件发送失败', error);
        return false;
    }
};

/**
 * 跟踪页面浏览
 */
export const trackPageView = () => {
    return trackEvent('PageView');
};

/**
 * 启用/禁用跟踪
 * @param {boolean} enabled 是否启用
 */
export const setTrackingEnabled = (enabled) => {
    isEnabled = enabled;

    if (config.debug) {
        console.log('Meta Pixel: 跟踪', enabled ? '已启用' : '已禁用');
    }
};

/**
 * 检查是否已初始化
 * @returns {boolean} 初始化状态
 */
export const isPixelInitialized = () => {
    return isInitialized;
};

/**
 * 检查跟踪是否启用
 * @returns {boolean} 启用状态
 */
export const isTrackingEnabled = () => {
    return isEnabled;
};

/**
 * Vue3 Composition API 钩子
 * @param {Object} options 配置选项
 * @returns {Object} 工具方法
 */
export const useFbPixel = (options = {}) => {
    // 合并配置
    const localConfig = { ...config, ...options };

    // 自动初始化
    if (localConfig.autoInit && !isInitialized && localConfig.pixelId && hasAdConsent()) {
        initFbPixel(localConfig);
    }

    return {
        // 状态
        isInitialized: isPixelInitialized,
        isEnabled: isTrackingEnabled,

        // 方法
        init: initFbPixel,
        trackEvent,
        trackCustomEvent,
        trackPageView,
        setEnabled: setTrackingEnabled
    };
};

// 预定义的常用事件
export const StandardEvents = {
    PAGE_VIEW: 'PageView',
    VIEW_CONTENT: 'ViewContent',
    SEARCH: 'Search',
    ADD_TO_CART: 'AddToCart',
    ADD_TO_WISHLIST: 'AddToWishlist',
    INITIATE_CHECKOUT: 'InitiateCheckout',
    ADD_PAYMENT_INFO: 'AddPaymentInfo',
    PURCHASE: 'Purchase',
    LEAD: 'Lead',
    COMPLETE_REGISTRATION: 'CompleteRegistration',
    CONTACT: 'Contact',
    CUSTOMIZE_PRODUCT: 'CustomizeProduct',
    DONATE: 'Donate',
    FIND_LOCATION: 'FindLocation',
    SCHEDULE: 'Schedule',
    START_TRIAL: 'StartTrial',
    SUBMIT_APPLICATION: 'SubmitApplication',
    SUBSCRIBE: 'Subscribe'
};

// 默认导出
export default useFbPixel;